import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import SubmissionProvider from 'pages/InstructorViewSubmission/PageProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import quizService from 'services/quiz';
import submissionService from 'services/submission';
import { Button, Loading, Stack, Text } from 'shell/components';
import { resolveFeatureFlags } from 'utils/helper';
import { getViewableQuizSubmissions } from 'utils/quizSubmissionList';

const PageProvider = ({ children }) => {
    const { department, studentAccountUsers } = useApp();
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const { id, submissionId } = useParams();
    const history = useHistory();
    const { t } = useTranslation('quiz');
    const [data, setData] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const scope = useRef(0);
    const saving = useRef(false);
    const scopeKey = `${id}:${auth.course_id}:${auth.owner_account_id}:${auth.role}`;

    const load = useCallback(async () => {
        const request = ++scope.current;
        setData(null);
        setLoadFailed(false);
        setIsSaving(false);
        saving.current = false;
        try {
            const [quiz, submissions] = await Promise.all([
                quizService.getQuiz(id),
                quizService.listQuizSubmissions(id),
            ]);
            if (request !== scope.current) return;
            if (
                quiz?.id !== id ||
                quiz.course_id !== auth.course_id ||
                !Array.isArray(quiz.rubric?.criteria) ||
                !Array.isArray(submissions)
            ) {
                throw new Error('Invalid quiz submission data');
            }
            setData({ quiz, submissions, scopeKey });
        } catch {
            if (request === scope.current) setLoadFailed(true);
        }
    }, [auth.course_id, id, scopeKey]);

    useEffect(() => {
        load();
        return () => {
            scope.current += 1;
        };
    }, [load]);

    const saveSubmission = useCallback(
        async (submission) => {
            if (saving.current) return false;
            saving.current = true;
            setIsSaving(true);
            const request = scope.current;
            const payload = {
                grading_status: 'confirmed',
                grading: submission.grading,
                grade_overrides: submission.grade_overrides ?? {},
                grading_explanations: submission.grading_explanations ?? {},
                ...(submission.sessions && { sessions: submission.sessions }),
            };
            try {
                const saved = await submissionService.updateSubmission(submission.id, payload);
                if (request !== scope.current) return false;
                setData((current) => ({
                    ...current,
                    submissions: current.submissions.map((item) =>
                        item.id === submission.id ? { ...item, ...submission, ...payload, ...saved } : item
                    ),
                }));
                return true;
            } catch {
                if (request === scope.current) {
                    message.error(t('submission.save-error', 'Changes could not be saved. Please try again.'));
                }
                return false;
            } finally {
                if (request === scope.current) {
                    saving.current = false;
                    setIsSaving(false);
                }
            }
        },
        [message, t]
    );

    const quiz = useMemo(
        () => (data?.quiz ? resolveFeatureFlags({ ...data.quiz, type: 'quiz' }, department?.features) : null),
        [data?.quiz, department?.features]
    );
    const submissionData = useMemo(
        () => ({
            currentAssignment: quiz,
            submissions: data?.submissions,
            studentAccountUsers,
            resourcePath: '/quizzes',
            isSaving,
            saveSubmission,
        }),
        [data?.submissions, isSaving, quiz, saveSubmission, studentAccountUsers]
    );
    const onClickBack = () => history.push(`/quizzes/${id}/submissions`);

    if (loadFailed) {
        return (
            <Stack alignItems={'center'} spacing={4} sx={{ p: 6 }}>
                <Text role={'alert'}>
                    {t('submission.load-error', 'The quiz submission could not be loaded. Please try again.')}
                </Text>
                <Button onClick={load}>{t('common:controls.retry', 'Retry')}</Button>
                <Button variant={'borderless'} onClick={onClickBack}>
                    {t('common:controls.back', 'Back')}
                </Button>
            </Stack>
        );
    }
    if (!data || data.scopeKey !== scopeKey) return <Loading />;
    if (!getViewableQuizSubmissions(data.submissions).some(({ id }) => id === submissionId)) {
        return (
            <Stack alignItems={'center'} spacing={4} sx={{ p: 6 }}>
                <Text role={'status'}>
                    {t('submission.unavailable', 'This submission is unavailable or has not been confirmed yet.')}
                </Text>
                <Button onClick={onClickBack}>{t('common:controls.back', 'Back')}</Button>
            </Stack>
        );
    }

    return (
        <SubmissionProvider key={`${scopeKey}:${submissionId}`} submissionData={submissionData}>
            {children}
        </SubmissionProvider>
    );
};

export default PageProvider;
